import { Suspense, lazy } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'

import '~/styles/globals.css' // keep global styles above other imports, does not apply properly otherwise
import SuspenseLoader from '~/core/SuspenseLoader'
import initiateAppRefresh from '~/lib/initiateAppRefresh'
import { getenv } from '~/settings'

import App from './AppContainer'

const GraphiQL = lazy(() => import('./GraphiQL'))
const GraphiQLWrapper = () => {
    return (
        <Suspense fallback={<SuspenseLoader />}>
            <GraphiQL />
        </Suspense>
    )
}

export function init(domRoot) {
    if (!domRoot) {
        throw new Error('Missing dom root')
    }
    initiateAppRefresh()

    const env = getenv('SENTRY_CURRENT_ENV')
    const root = createRoot(domRoot)

    const shouldDisablePlayground = env === 'production' || env === 'staging'

    const router = createBrowserRouter([
        ...(shouldDisablePlayground
            ? [
                  {
                      path: '/app/_internal/playground',
                      element: <GraphiQLWrapper />,
                  },
              ]
            : []),
        {
            path: '/*',
            element: <App />,
        },
    ])

    const debug = process.env.NODE_ENV !== 'production' // eslint-disable-line

    Sentry.init({
        dsn: getenv('SENTRY_FRONTEND_DSN'),
        integrations: [],
        environment: getenv('SENTRY_CURRENT_ENV'),
        release: __RELEASE_ID__,
        debug,
        tracesSampleRate: 1.0,
        ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'Non-Error promise rejection captured',
            'Failed to complete payment: Error: Your card was declined.',
            'Failed to complete payment: Error: Your card does not support this type of purchase.',
            'Failed to complete payment: Error: Your card has insufficient funds.',
            'Failed to complete payment: Error: Your card has expired.',
            'Failed to complete payment: Error: Failed to charge credit card',
        ],
    })

    root.render(<RouterProvider router={router} />)

    return Promise.resolve()
}
